import "./home.scss";

import Sidebar from "../../../components/sidebar/sidebar";
import Wallets from "../../../components/wallets/wallets";
import HomeUsdt from "../../../components/chart/home-usdt";
import Quotation from "../../../components/quotation/quotation";
import History from "../../../components/history/history";
import { useNavigate } from "react-router-dom";
import {
  BanknoteArrowDown,
  BanknoteArrowUp,
  Building2,
  ChevronRight,
  Clock,
  EyeOff,
  LayoutDashboardIcon,
  PiggyBank,
  SquareArrowOutUpRight,
  TrendingUp,
  Wallet2Icon,
} from "lucide-react";
import Swap from "../../../assets/images/logos/ads-swap.png";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import {
  fetchDepositsByUser,
  fetchUserWallets,
  UserStatusEnum,
} from "../../../store/UserSlice";
import { useEffect, useRef, useState } from "react";
import CardSkeleton from "../../../components/skeleton/CardSkeleton";
import SkeletonLine from "../../../components/skeleton/SkeletonLine";
import { Deposit } from "../../../models/Depoist";
import { fetchClientDepositAccounts } from "../../../store/DepositAccountSlice";
import { DepositAccountType } from "../../../models/DepositAccount";
import { DepositsService } from "../../../services/DepositService";
import { formatNumberBR } from "../../../utils/format";

// Design system
import {
  PageCard,
  SectionTitle,
  EmptyState,
  KpiCard,
  HeroBalance,
  CopyButton,
  Alert,
} from "../../../components/ui";

// A lista de contas para deposito cresce junto com o numero de empresas
// vinculadas e empurra o resto da coluna para baixo. Passando de 5 contas o
// bloco vira uma area rolavel.
const ACC_VISIVEIS = 5;
// Sobra deixada apos o corte, para o cartao seguinte espiar e sinalizar que
// ainda ha lista abaixo.
const ACC_ESPIA_PX = 26;

const Home = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const userInfo                                = useAppSelector((s) => s.user.userInfo);
  const wallets: Array<any> | null              = useAppSelector((s) => s.user.wallets);
  const deposits: Array<Deposit>                = useAppSelector((s) => s.user.deposits);
  const withdrawls: Array<Deposit>              = useAppSelector((s) => s.deposit.depositsByUser);
  const clientGroups                            = useAppSelector((s) => s.depositAccount.clientGroups);

  const [pending, setPending]          = useState<number>(0);
  const [last, setLast]                = useState<string>("");
  const [depositList, setDepositList]  = useState<any[]>([]);
  const [totalDeposited, setDeposited] = useState<number>(0);
  const [totalWithdrawn, setWithdrawn] = useState<number>(0);
  const [loaded, setLoaded]            = useState<boolean>(false);
  const [accMaxH, setAccMaxH]          = useState<number | undefined>(undefined);

  const accBoxRef   = useRef<HTMLDivElement>(null);
  const totalContas = clientGroups?.reduce((n, g) => n + g.accounts.length, 0) ?? 0;

  // A altura do corte e medida no DOM, e nao fixada em px, porque os cartoes
  // tem alturas diferentes: o FIAT mostra banco/conta/agencia em linhas
  // curtas e o CRYPTO mostra um hash que quebra em varias linhas. Medindo,
  // o corte cai sempre no mesmo ponto da lista independente da mistura.
  useEffect(() => {
    const box = accBoxRef.current;
    if (!box || totalContas <= ACC_VISIVEIS) {
      setAccMaxH(undefined);
      return;
    }

    const medir = () => {
      const corte = box.querySelectorAll<HTMLElement>(".account-card")[ACC_VISIVEIS];
      if (!corte) return;
      // `scrollTop` entra na conta porque a remedicao pode acontecer com a
      // lista ja rolada — sem ele o corte subiria a cada resize.
      const topo = corte.getBoundingClientRect().top - box.getBoundingClientRect().top;
      setAccMaxH(Math.round(topo + box.scrollTop + ACC_ESPIA_PX));
    };

    medir();
    // Mudar a largura (rotacionar o celular, arrastar a janela) faz o hash e
    // os nomes reQuebrarem, mudando a altura dos cartoes.
    const observer = new ResizeObserver(medir);
    observer.observe(box);
    return () => observer.disconnect();
  }, [clientGroups, totalContas]);

  // Re-roda quando o usuario passa a ter organizacao (ex.: logo apos aceitar o
  // convite, o userInfo ganha a org) — antes dependia so de [dispatch] e nao
  // buscava as carteiras quando o vinculo chegava depois do mount.
  const orgCount = userInfo.organizationUsers?.length ?? 0;

  useEffect(() => {
    if (orgCount > 0) {
      dispatch(fetchUserWallets(userInfo.id));
      dispatch(fetchDepositsByUser(userInfo.id));
    }
    // Roda mesmo sem empresa: o thunk seta [] e a home mostra o estado vazio.
    dispatch(fetchClientDepositAccounts());
  }, [dispatch, userInfo.id, orgCount]);

  useEffect(() => {
    if (!deposits) return;
    setLoaded(true);
    if (deposits.length === 0) return;

    let pendingTotal = 0;
    let depositedTotal = 0;
    deposits.forEach((d: Deposit) => {
      if (d.status === "waiting") pendingTotal   += +d.value;
      else                         depositedTotal += +d.value;
    });

    setPending(pendingTotal);
    setDeposited(depositedTotal);
    setLast(deposits[deposits.length - 1].updatedAt);
    setDepositList(deposits);
  }, [deposits]);

  useEffect(() => {
    if (!withdrawls) return;
    const total = withdrawls.reduce((acc, w) => acc + +w.value, 0);
    setWithdrawn(total);
  }, [withdrawls]);

  // Creditos pendentes: valor adiantado pela empresa que o cliente ainda
  // precisa pagar. Enquanto houver algum, a home mostra um aviso.
  const [pendingCredits, setPendingCredits] = useState<any[]>([]);

  useEffect(() => {
    if (orgCount === 0) return;
    DepositsService.getMyPendingCredits()
      .then((list) => setPendingCredits(Array.isArray(list) ? list : []))
      .catch((e) => { console.error(e); setPendingCredits([]); });
  }, [orgCount]);

  // Total por moeda ("R$ 1.000,00 + USDT 50,00") — creditos podem ser de
  // assets diferentes.
  const pendingCreditsText = Object.entries(
    pendingCredits.reduce<Record<string, number>>((acc, c) => {
      const symbol = c.asset?.symbol ?? "";
      acc[symbol] = (acc[symbol] ?? 0) + Number(c.value ?? 0);
      return acc;
    }, {}),
  ).map(([symbol, total]) => `${symbol} ${formatNumberBR(total)}`.trim()).join(" + ");

  const balance = wallets ? Number(wallets[0]?.balance ?? 0) : undefined;

  return (
    <div className="home-wrapper">
      <Sidebar />
      <div className="dashboard">

        <h1 className="title">
          <div>
            <LayoutDashboardIcon size={24} className="icon" /> Area do Cliente
          </div>
          <small>dashboard de controle do cliente. aqui voce pode controlar suas financas na TRK.</small>
        </h1>

        {pendingCredits.length > 0 && (
          <Alert variant="warning" className="pending-credits-alert">
            Voce tem {pendingCredits.length === 1 ? "1 credito pendente" : `${pendingCredits.length} creditos pendentes`}{" "}
            no total de <strong>{pendingCreditsText}</strong>. Esse valor foi adiantado e precisa ser pago.
          </Alert>
        )}

        {/* HERO componentizado */}
        <HeroBalance
          label="Saldo total disponivel"
          balance={balance}
          pending={loaded ? pending : undefined}
          lastTx={last || undefined}
          actions={
            <>
              <button className="btn-pill ghost">
                <BanknoteArrowUp size={16} /> Solicitar
              </button>
              <button className="btn-pill primary">
                <BanknoteArrowDown size={16} /> Sacar
              </button>
            </>
          }
        />

        {/* KPIs componentizados */}
        <section className="kpi-row">
          <KpiCard variant="success" icon={PiggyBank}
            label="Total depositado"
            value={loaded ? totalDeposited.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : <SkeletonLine height="22px" />}
          />
          <KpiCard variant="danger" icon={BanknoteArrowDown}
            label="Total sacado"
            value={totalWithdrawn.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
          />
          <KpiCard variant="warn" icon={Clock}
            label="Aguardando confirmacao"
            value={loaded ? pending.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : <SkeletonLine height="22px" />}
          />
          <KpiCard variant="info" icon={TrendingUp}
            label="Carteiras ativas"
            value={wallets ? (wallets.length ?? 0) : <SkeletonLine height="22px" />}
          />
        </section>

        <div className="columns">

          {/* coluna 1 */}
          <div className="column">
            <PageCard>
              <SectionTitle
                icon={Wallet2Icon}
                actions={
                  <button className="link-more" onClick={() => navigate("/listar-carteiras")}>
                    Mais detalhes <ChevronRight size={14} />
                  </button>
                }
              >
                Suas carteiras
              </SectionTitle>
              {wallets && wallets.length > 0 && <Wallets wallets={wallets} />}
              {wallets && wallets.length === 0 && (
                <EmptyState
                  icon={Wallet2Icon}
                  title="Voce ainda nao tem carteiras cadastradas."
                  description="Fale com o seu gerente de conta para liberar a primeira."
                />
              )}
              {!wallets && <CardSkeleton />}
            </PageCard>

            <PageCard><Quotation /></PageCard>
          </div>

          {/* coluna 2 */}
          <div className="column">
            <PageCard><HomeUsdt /></PageCard>

            <PageCard className="ads">
              <h2>SWAP TRKBIT</h2>
              <h3>Seu novo jeito de vendas na internet</h3>
              <img src={Swap} alt="" />
              <p>Comece hoje a utilizar o SWAP TRKBIT. O novo modelo de vendas que revolucionara o jeito que pequenos comerciantes farao as suas vendas.</p>
              <button className="btn-pill ghost">
                Conhecer <SquareArrowOutUpRight size={14} />
              </button>
            </PageCard>
          </div>

          {/* coluna 3 */}
          <div className="column">
            <PageCard>
              <SectionTitle icon={Clock}>Atividade recente</SectionTitle>
              {depositList && depositList.length > 0 && <History list={depositList} showMore={false} />}
              {depositList && depositList.length === 0 && (
                <EmptyState
                  icon={Clock}
                  title="Sem transacoes por aqui ainda."
                  description="Quando voce fizer um deposito ele aparece aqui."
                />
              )}
              {!depositList && <SkeletonLine height="40" />}
            </PageCard>

            <PageCard>
              <SectionTitle icon={BanknoteArrowDown}>Contas para deposito</SectionTitle>

              {!clientGroups && <SkeletonLine height="40" />}

              {clientGroups && !clientGroups.some((g) => g.accounts.length > 0) && (
                <EmptyState
                  icon={BanknoteArrowDown}
                  title="Sem nenhuma conta disponivel"
                  description="As empresas vinculadas ainda nao cadastraram contas para deposito."
                />
              )}

              {clientGroups && clientGroups.some((g) => g.accounts.length > 0) && (
                <div
                  ref={accBoxRef}
                  className={`acc-groups${accMaxH ? " scrollable" : ""}`}
                  style={accMaxH ? { maxHeight: accMaxH } : undefined}
                >
                  {clientGroups
                    .filter((group) => group.accounts.length > 0)
                    .map((group) => (
                      <div className="acc-group" key={group.organizationId}>
                        {/* nome da empresa como label principal */}
                        <span className="acc-group-label">
                          <Building2 size={15} /> {group.organizationName}
                        </span>

                        <ul className="acc-list">
                          {group.accounts.map((acc) => (
                            <li className="account-card" key={acc.id}>

                              <div className="acc-title">
                                <div>
                                  <i>
                                    {acc.type === DepositAccountType.CRYPTO ? "C" : "F"}
                                    <div className="hover">{acc.type}</div>
                                  </i>
                                  <strong>{acc.nickname}</strong>
                                </div>
                                <span>
                                  {acc.type === DepositAccountType.CRYPTO ? "CRYPTO" : acc.transfer_type}
                                </span>
                              </div>

                              {acc.type === DepositAccountType.FIAT ? (
                                <>
                                  <div className="pix-info">
                                    Banco: <strong>{acc.bank?.name}</strong>
                                  </div>
                                  <div className="flex bank-data">
                                    <span>Conta</span> <span>Agencia</span>
                                  </div>
                                  <div className="flex bank-data number num">
                                    <span className="acc-field">
                                      {acc.account}
                                      <CopyButton value={acc.account ?? ""} size={13} title="Copiar conta" />
                                    </span>
                                    <span className="acc-field">
                                      {acc.agency}
                                      <CopyButton value={acc.agency ?? ""} size={13} title="Copiar agencia" />
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="pix-info">
                                    Rede: <strong>{acc.network?.name}</strong>
                                  </div>
                                  <div className="flex bank-data">
                                    <span>Hash da carteira</span>
                                  </div>
                                  <div className="acc-hash num">
                                    <span className="acc-hash-text">{acc.hash}</span>
                                    <CopyButton value={acc.hash ?? ""} title="Copiar hash" />
                                  </div>
                                </>
                              )}

                              {userInfo.status === UserStatusEnum.INACTIVE && (
                                <div className="shadow">
                                  <div className="blur"></div>
                                  <EyeOff className="icon" />
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              )}
            </PageCard>
          </div>

        </div>

      </div>
    </div>
  );
};

export default Home;